<template>
  <v-container fluid grid-list-xs>

    <!-- Header -->
    <v-layout row wrap mb-2 class="grey lighten-4">
      <v-flex>
        <h2>
          <router-link to="/terminals">Terminals</router-link>
          <span class="grey--text">&gt;</span>
          {{ terminal.name }} <span class="grey--text">(</span>{{ terminal.id }}<span class="grey--text">)</span>
        </h2>
      </v-flex>

      <v-flex v-if="terminalLoaded" class="text-sm-right" shrink>
        <v-btn color="error" @click="setActive(0)" round small v-if="terminal.active">Deactivate</v-btn>
        <v-btn color="success" @click="setActive(1)" round small v-if="!terminal.active">Activate</v-btn>
      </v-flex>
    </v-layout>

    <!-- Read-only status info -->
    <v-layout row wrap>
      <v-flex xs6 sm2 class="primary--text pa-1 font-weight-black">Account:</v-flex>
      <v-flex v-if="terminalLoaded" xs6 sm4 class="pa-1">
        <router-link :to="{ name: 'Merchant', params: { id: terminal.account_id } }">{{ terminal.account_name }}</router-link>
      </v-flex>

      <v-flex xs6 sm2 class="primary--text pa-1 font-weight-black">Code:</v-flex>
      <v-flex v-if="terminalLoaded" xs6 sm4 class="pa-1">{{ terminal.code }}</v-flex>

      <v-flex xs6 sm2 class="primary--text pa-1 font-weight-black">Active:</v-flex>
      <v-flex v-if="terminalLoaded" xs6 sm4 :class="(!terminal.active) ? 'error pa-1' : 'pa-1'">{{ terminal.active ? 'Yes' : 'No' }}</v-flex>

      <v-flex xs6 sm2 class="primary--text pa-1 font-weight-black">Virtual:</v-flex>
      <v-flex v-if="terminalLoaded" xs6 sm4 class="pa-1">{{ terminal.is_virtual ? 'Yes' : 'No' }}</v-flex>

      <v-flex xs6 sm2 class="primary--text pa-1 font-weight-black">Testing:</v-flex>
      <v-flex v-if="terminalLoaded" xs6 sm4 class="pa-1">{{ terminal.is_testing ? 'Yes' : 'No' }}</v-flex>

      <v-flex xs6 sm2 class="primary--text pa-1 font-weight-black">Processor:</v-flex>
      <v-flex v-if="terminalLoaded" xs6 sm4 class="pa-1">{{ terminal.processor_name }}</v-flex>

      <v-flex xs6 sm2 class="primary--text pa-1 font-weight-black">Created:</v-flex>
      <v-flex v-if="terminalLoaded" xs6 sm4 class="pa-1">{{ datetime(terminal.created) }}</v-flex>

      <v-flex xs6 sm2 class="primary--text pa-1 font-weight-black">Modified:</v-flex>
      <v-flex v-if="terminalLoaded" xs6 sm4 class="pa-1">{{ datetime(terminal.modified) }}</v-flex>
    </v-layout>

    <v-divider class="primary"></v-divider>

    <v-form v-model="isFormValid" ref="form" @submit.prevent>
      <v-expansion-panel
        v-model="panel"
        expand
        multiple
        class="elevation-0"
      >

        <!-- Basic Information Panel -->
        <v-expansion-panel-content>
          <div slot="header" class="title font-weight-medium">Basic Information</div>
          <v-card flat>
            <v-card-text>
              <v-layout row wrap>
                <v-flex xs12 sm6 lg4>
                  <v-text-field
                    label="Name"
                    v-model="fields.name"
                    box

                    required
                    :rules="[ v => !!v || 'Name must be provided' ]"
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm6 lg4>
                  <v-text-field
                    label="Code"
                    v-model="fields.code"
                    box
                    required
                    :rules="[
                      v => !!v || 'Code must be provided',
                      v => (v && v.length >= 3 && v.length <= 12) || 'Code must be 3-12 characters'
                    ]"
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm6 lg4>
                  <v-combobox
                    label="Type"
                    v-model="fields.type"
                    :items="['retail', 'ecom', 'moto', 'retail/ecom', 'retail/moto', 'ecom/moto', 'retail/ecom/moto']"
                    box
                  ></v-combobox>
                </v-flex>

                <v-flex xs12 sm6 lg4>
                  <v-select
                    label="API"
                    v-model="fields.api"
                    :items="apiOptions"
                    box
                  ></v-select>
                </v-flex>

                <v-flex xs12 sm6 lg4>
                  <v-select
                    label="Processor"
                    v-model="fields.processor_id"
                    :items="processors"
                    item-text="name"
                    item-value="id"
                    box
                    clearable
                  ></v-select>
                </v-flex>

                <v-flex xs12 sm6 lg4>
                  <v-text-field
                    label="Key"
                    v-model="fields.key"
                    box
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm6 lg4>
                  <v-text-field
                    label="Password"
                    v-model="fields.password"
                    box
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm6 lg4>
                  <v-text-field
                    label="Serial Number"
                    v-model="fields.serial_number"
                    box
                  ></v-text-field>
                </v-flex>
              </v-layout>

              <v-layout row wrap>
                <v-flex xs12 sm4>
                  <v-checkbox
                    label="Active"
                    v-model="fields.active"
                    color="primary"
                  ></v-checkbox>
                </v-flex>
                <v-flex xs12 sm4>
                  <v-checkbox
                    label="Testing"
                    v-model="fields.is_testing"
                    color="primary"
                  ></v-checkbox>
                </v-flex>
                <v-flex xs12 sm4>
                  <v-checkbox
                    label="Virtual"
                    v-model="fields.is_virtual"
                    color="primary"
                  ></v-checkbox>
                </v-flex>
                <v-flex xs12 sm4>
                  <v-checkbox
                    label="Split"
                    v-model="fields.is_split"
                    color="primary"
                  ></v-checkbox>
                </v-flex>
              </v-layout>

              <v-layout row wrap align-center>
                <h3 class="pa-2 primary--text">Address</h3>
                <v-divider class="primary"></v-divider>
              </v-layout>

              <v-layout row wrap>
                <v-flex xs12 sm6>
                  <v-text-field
                    label="Address"
                    v-model="fields.address"
                    box
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 lg3>
                  <v-text-field
                    label="City"
                    v-model="fields.city"
                    box
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 lg3>
                  <v-text-field
                    label="State"
                    v-model="fields.state"
                    box
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 lg3>
                  <v-text-field
                    label="Postal Code"
                    v-model="fields.postal_code"
                    box
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 lg3>
                  <v-text-field
                    label="Country"
                    v-model="fields.country"
                    box
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 lg3>
                  <v-text-field
                    label="Phone"
                    v-model="fields.phone"
                    box
                  ></v-text-field>
                </v-flex>
              </v-layout>

              <v-layout row wrap justify-end>
                <v-btn color="primary" outline @click.stop="reset" :disabled="!hasAnyFieldChanged">Reset</v-btn>
                <v-btn color="primary" @click.stop="save" :disabled="!hasAnyFieldChanged" :loading="isProcessing">Save</v-btn>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-expansion-panel-content>

        <!-- Users Panel -->
        <v-expansion-panel-content>
          <div slot="header" class="title font-weight-medium">
            Users ({{ terminalUsers.length }})
          </div>
          <v-card flat>
            <v-card-text>
              <v-layout row wrap align-center mb-3>
                <v-flex>
                  <span class="subheading">Manage user access for this terminal</span>
                </v-flex>
                <v-flex shrink>
                  <v-btn small color="primary" @click="showAddUserModal = true" :disabled="!terminalLoaded">
                    <v-icon small left>fa-plus</v-icon>
                    Add User
                  </v-btn>
                </v-flex>
              </v-layout>

              <v-data-table
                :headers="userHeaders"
                :items="terminalUsers"
                :loading="loadingUsers"
                hide-actions
                class="elevation-1"
              >
                <template v-slot:items="props">
                  <td>{{ props.item.user_id }}</td>
                  <td>{{ props.item.user_name }}</td>
                  <td>{{ props.item.user_email }}</td>
                  <td>{{ props.item.user_position }}</td>
                  <td>
                    <v-checkbox
                      :input-value="props.item.is_default"
                      @change="updateUserFlag(props.item, 'is_default', $event)"
                      color="primary"
                      hide-details
                      class="mt-0"
                    ></v-checkbox>
                  </td>
                  <td>
                    <v-checkbox
                      :input-value="props.item.can_charge"
                      @change="updateUserFlag(props.item, 'can_charge', $event)"
                      color="primary"
                      hide-details
                      class="mt-0"
                    ></v-checkbox>
                  </td>
                  <td>
                    <v-chip small :color="props.item.user_enabled ? 'success' : 'error'" text-color="white">
                      {{ props.item.user_enabled ? 'Active' : 'Disabled' }}
                    </v-chip>
                  </td>
                  <td class="text-xs-right">
                    <v-btn small flat color="error" @click="removeUser(props.item)">
                      <v-icon small left>fa-trash</v-icon>
                      Remove
                    </v-btn>
                  </td>
                </template>
                <template v-slot:no-data>
                  <v-alert :value="true" type="info">
                    No users assigned to this terminal
                  </v-alert>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-expansion-panel-content>

      </v-expansion-panel>
    </v-form>

    <AddUserModal
      v-model="showAddUserModal"
      :terminalId="terminal.id"
      :terminalAccountId="terminal.account_id"
      :existingUserIds="existingUserIds"
      @added="loadUsers"
    ></AddUserModal>

  </v-container>
</template>

<script>
import Bus from '@/bus'

import AddUserModal from '@/components/manage/terminal/AddUserModal'

import ApiMixin from '@/mixins/api'
import AuthMixin from '@/mixins/auth'
import DisplayMixin from '@/mixins/display'
import StoreMixin from '@/store/mixin'

export default {
  components: { AddUserModal },
  mixins: [ApiMixin, AuthMixin, DisplayMixin, StoreMixin],

  data: function () {
    return {
      panel: [true, false],
      terminal: {},
      terminalLoaded: false,

      isFormValid: false,
      isProcessing: false,
      fields: this.defaultFormFields(),

      // Users
      terminalUsers: [],
      loadingUsers: false,
      showAddUserModal: false,
      userHeaders: [
        { text: 'ID', value: 'user_id', sortable: false },
        { text: 'Name', value: 'user_name', sortable: false },
        { text: 'Email', value: 'user_email', sortable: false },
        { text: 'Position', value: 'user_position', sortable: false },
        { text: 'Default', value: 'is_default', sortable: false },
        { text: 'Can Charge', value: 'can_charge', sortable: false },
        { text: 'Status', value: 'user_enabled', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false, align: 'right' }
      ],

      processors: [],

      apiOptions: ['jetpay', 'paynetworx', 'paymentspring', 'trxservices', 'fpg']
    }
  },

  computed: {
    existingUserIds () {
      return this.terminalUsers.map(u => u.user_id)
    },

    hasAnyFieldChanged () {
      for (const field in this.fields) {
        if (this.hasFieldChanged(field)) return true
      }
      return false
    }
  },

  mounted () {
    this.load()
    this.loadProcessors()
  },

  beforeRouteUpdate (to, from, next) {
    this.reset()
    next()
  },

  watch: {
    $route () {
      this.reset()
    }
  },

  methods: {
    defaultFormFields () {
      return {
        name: '',
        code: '',
        type: '',
        api: 'jetpay',
        processor_id: null,
        key: '',
        password: '',
        serial_number: '',
        active: true,
        is_testing: false,
        is_virtual: false,
        is_split: false,
        address: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
        phone: ''
      }
    },

    hasFieldChanged (field) {
      if (!this.terminalLoaded) return false
      const original = this.terminal[field]
      const current = this.fields[field]
      // Normalize booleans (DB returns 0/1)
      if (typeof current === 'boolean') {
        return (current ? 1 : 0) !== Number(original)
      }
      return String(current || '') !== String(original || '')
    },

    populateFields (terminalData) {
      this.fields = this.defaultFormFields()
      for (const [key, value] of Object.entries(terminalData)) {
        if (this.fields.hasOwnProperty(key)) {
          if (typeof this.fields[key] === 'boolean') {
            this.fields[key] = !!value
          } else {
            this.fields[key] = value !== null && value !== undefined ? value : ''
          }
        }
      }
    },

    async load () {
      Bus.$emit('openWaitModal')

      try {
        if (!this.$route.params.id) {
          throw new Error('Invalid terminal')
        }

        let data = await this.api({ url: `/terminals/${this.$route.params.id}` })
        this.terminal = data.data.terminal
        this.populateFields(this.terminal)
        this.terminalLoaded = true

        await this.loadUsers()
      } catch (error) {
        Bus.$emit('openModal', { status: 'error', message: error })
      } finally {
        Bus.$emit('closeWaitModal')
      }
    },

    async loadUsers () {
      this.loadingUsers = true
      try {
        let data = await this.api({ url: `/terminals/${this.$route.params.id}/users` })
        this.terminalUsers = data.data.users
      } catch (error) {
        Bus.$emit('openModal', { status: 'error', message: error })
      } finally {
        this.loadingUsers = false
      }
    },

    async loadProcessors () {
      try {
        let data = await this.api({ url: '/processors/list' })
        this.processors = data.data.rows
      } catch (error) {
        console.error('Error loading processors:', error)
      }
    },

    reset () {
      this.terminalLoaded = false
      this.terminal = {}
      this.terminalUsers = []
      this.fields = this.defaultFormFields()
      this.load()
    },

    async save () {
      if (!this.$refs.form.validate()) return

      this.isProcessing = true
      try {
        let terminal = {
          ...this.fields,
          id: this.terminal.id
        }

        await this.api({
          method: 'put',
          url: `/terminals/${this.terminal.id}`,
          data: { terminal, user_ids: this.existingUserIds }
        })

        Bus.$emit('openModal', { status: 'success', message: 'Terminal saved' })
        await this.load()
      } catch (error) {
        Bus.$emit('openModal', { status: 'error', message: error })
      } finally {
        this.isProcessing = false
      }
    },

    async setActive (value) {
      try {
        Bus.$emit('openWaitModal')
        await this.api({
          method: 'put',
          url: `/terminals/${this.terminal.id}`,
          data: {
            terminal: { ...this.terminal, active: value },
            user_ids: this.existingUserIds
          }
        })
        await this.load()
      } catch (error) {
        Bus.$emit('openModal', { status: 'error', message: error })
      } finally {
        Bus.$emit('closeWaitModal')
      }
    },

    async updateUserFlag (user, flag, value) {
      try {
        await this.api({
          method: 'put',
          url: `/terminals/${this.terminal.id}/users/${user.user_id}`,
          data: { [flag]: value }
        })
        user[flag] = value
      } catch (error) {
        Bus.$emit('openModal', { status: 'error', message: error })
        await this.loadUsers()
      }
    },

    async removeUser (user) {
      try {
        await this.api({
          method: 'delete',
          url: `/terminals/${this.terminal.id}/users/${user.user_id}`
        })
        this.terminalUsers = this.terminalUsers.filter(u => u.user_id !== user.user_id)
      } catch (error) {
        Bus.$emit('openModal', { status: 'error', message: error })
      }
    }
  }
}
</script>

<style lang="less">
  .v-expansion-panel__container {
    margin: 5px;
  }
  .v-expansion-panel__header {
    background: var(--v-secondary-base) !important;
    padding: 5px;
  }

</style>



// WEBPACK FOOTER //
// src/components/manage/Terminal.vue